import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface Ticket {
  id: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: 'open' | 'closed';
  createdBy: string;
  studentName: string;
  className: string;
  section: string;
  enrollmentId: string;
  createdAt: Date;
  updatedAt?: Date;
  closedAt?: Date;
  resolution?: string;
}

interface TicketListProps {
  domain: string;
  statusFilter: 'open' | 'closed';
}

const TicketList: React.FC<TicketListProps> = ({ domain, statusFilter }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [resolution, setResolution] = useState('');
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!domain) return;

    const ticketsRef = collection(db, `school-data/${domain}/complaint-tickets`);
    const q = query(
      ticketsRef,
      where('status', '==', statusFilter),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const ticketData: Ticket[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        ticketData.push({
          id: docSnap.id,
          subject: data.subject || 'Untitled',
          description: data.description || '',
          category: data.category || 'General',
          priority: data.priority || 'Medium',
          status: data.status || 'open',
          createdBy: data.createdBy || '',
          studentName: data.studentName || 'Unknown',
          className: data.className || '',
          section: data.section || '',
          enrollmentId: data.enrollmentId || '',
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined,
          closedAt: data.closedAt instanceof Timestamp ? data.closedAt.toDate() : data.closedAt ? new Date(data.closedAt) : undefined,
          resolution: data.resolution || '',
        });
      });
      setTickets(ticketData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching tickets:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [domain, statusFilter]);

  const handleCloseTicket = async () => {
    if (!selectedTicket || !resolution.trim()) return;
    setClosing(true);
    try {
      const ticketRef = doc(db, `school-data/${domain}/complaint-tickets`, selectedTicket.id);
      await updateDoc(ticketRef, {
        status: 'closed',
        resolution: resolution.trim(),
        closedAt: Date.now(),
        updatedAt: Date.now(),
      });
      setSelectedTicket(null);
      setResolution('');
    } catch (error) {
      console.error('Error closing ticket:', error);
      alert('Failed to close ticket. Please try again.');
    } finally {
      setClosing(false);
    }
  };

  const handleReopenTicket = async (ticket: Ticket) => {
    try {
      const ticketRef = doc(db, `school-data/${domain}/complaint-tickets`, ticket.id);
      await updateDoc(ticketRef, {
        status: 'open',
        resolution: '',
        closedAt: null,
        updatedAt: Date.now(),
      });
    } catch (error) {
      console.error('Error reopening ticket:', error);
      alert('Failed to reopen ticket. Please try again.');
    }
  };

  const getPriorityBadge = (priority: string) => {
    const p = priority.toLowerCase();
    const colors: Record<string, string> = {
      low: 'bg-info',
      medium: 'bg-warning text-dark',
      high: 'bg-orange',
      urgent: 'bg-danger',
    };
    return colors[p] || 'bg-secondary';
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'Academic': 'bi-book',
      'Infrastructure': 'bi-building',
      'Transport': 'bi-bus-front',
      'Fee': 'bi-cash-coin',
      'Discipline': 'bi-shield-exclamation',
      'Hostel': 'bi-house',
      'General': 'bi-chat-dots',
      'Staff': 'bi-people',
    };
    return icons[category] || 'bi-tag';
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredTickets = tickets.filter((ticket) =>
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.enrollmentId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading tickets...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Row */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <h3 className="fw-bold mb-0" style={{ color: statusFilter === 'open' ? '#DC3545' : '#28A745' }}>
                {tickets.length}
              </h3>
              <small className="text-muted">{statusFilter === 'open' ? 'Open' : 'Closed'} Tickets</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <h3 className="fw-bold mb-0 text-danger">
                {tickets.filter(t => t.priority.toLowerCase() === 'urgent').length}
              </h3>
              <small className="text-muted">Urgent</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <h3 className="fw-bold mb-0 text-warning">
                {tickets.filter(t => t.priority.toLowerCase() === 'high').length}
              </h3>
              <small className="text-muted">High Priority</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <h3 className="fw-bold mb-0 text-info">
                {tickets.filter(t => t.priority.toLowerCase() === 'medium' || t.priority.toLowerCase() === 'low').length}
              </h3>
              <small className="text-muted">Normal</small>
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body py-2">
          <div className="input-group">
            <span className="input-group-text bg-transparent border-0">
              <i className="bi bi-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control border-0"
              placeholder="Search by subject, description, student name, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="btn btn-link text-muted" onClick={() => setSearchTerm('')}>
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tickets List */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-5">
          <i className={`bi ${statusFilter === 'open' ? 'bi-inbox' : 'bi-check-circle'} fs-1 text-muted d-block mb-3`}></i>
          <h5 className="text-muted">
            {searchTerm ? 'No tickets match your search' : `No ${statusFilter} tickets found`}
          </h5>
          <p className="text-muted small">
            {statusFilter === 'open' ? 'All caught up! No open complaints.' : 'No resolved tickets yet.'}
          </p>
        </div>
      ) : (
        <div className="row g-3">
          {filteredTickets.map((ticket) => (
            <div key={ticket.id} className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-start flex-grow-1">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{
                          width: '44px',
                          height: '44px',
                          backgroundColor: statusFilter === 'open' ? '#DC354520' : '#28A74520',
                          color: statusFilter === 'open' ? '#DC3545' : '#28A745',
                        }}
                      >
                        <i className={`bi ${getCategoryIcon(ticket.category)}`}></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          <h6 className="mb-0 fw-bold">{ticket.subject}</h6>
                          <span className={`badge ${getPriorityBadge(ticket.priority)} rounded-pill`} style={ticket.priority.toLowerCase() === 'high' ? { backgroundColor: '#FD7E14' } : {}}>
                            {ticket.priority}
                          </span>
                          <span className="badge bg-light text-dark border rounded-pill">
                            {ticket.category}
                          </span>
                        </div>
                        <p className="text-muted small mb-1" style={{ maxWidth: '600px' }}>
                          {ticket.description.length > 150 ? ticket.description.substring(0, 150) + '...' : ticket.description}
                        </p>
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                          <small className="text-muted">
                            <i className="bi bi-person me-1"></i>{ticket.studentName}
                          </small>
                          {ticket.className && (
                            <small className="text-muted">
                              <i className="bi bi-mortarboard me-1"></i>Class {ticket.className}{ticket.section ? `-${ticket.section}` : ''}
                            </small>
                          )}
                          <small className="text-muted">
                            <i className="bi bi-hash me-1"></i>{ticket.enrollmentId}
                          </small>
                          <small className="text-muted">
                            <i className="bi bi-clock me-1"></i>{formatDate(ticket.createdAt)}
                          </small>
                          {ticket.closedAt && (
                            <small className="text-success">
                              <i className="bi bi-check-circle me-1"></i>Closed: {formatDate(ticket.closedAt)}
                            </small>
                          )}
                        </div>
                        {ticket.resolution && (
                          <div className="mt-2 p-2 bg-light rounded">
                            <small className="text-muted fw-semibold">Resolution: </small>
                            <small className="text-muted">{ticket.resolution}</small>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ms-3 flex-shrink-0">
                      {statusFilter === 'open' ? (
                        <button
                          className="btn btn-outline-success btn-sm"
                          onClick={() => setSelectedTicket(ticket)}
                          title="Close Ticket"
                        >
                          <i className="bi bi-check-lg me-1"></i>Close
                        </button>
                      ) : (
                        <button
                          className="btn btn-outline-warning btn-sm"
                          onClick={() => handleReopenTicket(ticket)}
                          title="Reopen Ticket"
                        >
                          <i className="bi bi-arrow-counterclockwise me-1"></i>Reopen
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Close Ticket Modal */}
      {selectedTicket && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedTicket(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Close Ticket</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedTicket(null)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2"><strong>Ticket:</strong> {selectedTicket.subject}</p>
                <p className="mb-2 text-muted small">By {selectedTicket.studentName} (Class {selectedTicket.className}{selectedTicket.section ? `-${selectedTicket.section}` : ''})</p>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Resolution / Remarks *</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Enter resolution or closing remarks..."
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSelectedTicket(null)}>Cancel</button>
                <button
                  className="btn btn-success"
                  onClick={handleCloseTicket}
                  disabled={!resolution.trim() || closing}
                >
                  {closing ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Closing...</>
                  ) : (
                    <><i className="bi bi-check-lg me-1"></i>Close Ticket</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketList;
